// 2-4 showcase clips side by side (V4-MS), for the stage and the Director ("here's how Kling and Seedance handle fast
// action") and the Models screen's ?compare= link. One Play plays them all from the start, in step; only the focused
// one has sound (tap a clip to move the sound to it). Takes CompareClip (showcase.ts compareClips), so it works on any
// clips, not only the showcase's.
import { useEffect, useRef, useState } from "react";
import type { CompareClip } from "../showcase";
import { Icon } from "./bits";

export function ShowcaseCompare({ clips, title, onClose }: { clips: CompareClip[]; title?: string; onClose?: () => void }) {
  const refs = useRef<(HTMLVideoElement | null)[]>([]);
  const [playing, setPlaying] = useState(false);
  const [focus, setFocus] = useState(0);
  const shown = clips.slice(0, 4);

  useEffect(() => {
    refs.current.forEach((v, i) => v && (v.muted = i !== focus));
  }, [focus, shown.length]);

  if (shown.length < 2) return null;

  const playAll = () => {
    const vs = refs.current.filter((v): v is HTMLVideoElement => !!v);
    if (playing) {
      vs.forEach((v) => v.pause());
      setPlaying(false);
      return;
    }
    vs.forEach((v, i) => {
      v.currentTime = 0;
      v.muted = i !== focus;
      void v.play().catch(() => {
        // a browser that blocks sound on one: play it muted rather than not at all
        v.muted = true;
        void v.play().catch(() => {});
      });
    });
    setPlaying(true);
  };

  return (
    <section className={`scmp scmp-${shown.length}`} aria-label={title ?? "Side by side"}>
      {(title || onClose) && (
        <header className="scmp-head">
          {title && <h3 className="scmp-title">{title}</h3>}
          {onClose && (
            <button type="button" className="icon-btn" onClick={onClose} aria-label="Close the comparison">
              <Icon name="close" size={18} />
            </button>
          )}
        </header>
      )}
      <div className="scmp-grid">
        {shown.map((c, i) => (
          <figure key={c.key} className={`scmp-clip${i === focus ? " is-focus" : ""}`}>
            <button type="button" className="scmp-frame" onClick={() => setFocus(i)} aria-pressed={i === focus} aria-label={`${c.title}${i === focus ? ", sound on" : ", tap for its sound"}`}>
              <video
                ref={(el) => {
                  refs.current[i] = el;
                }}
                src={c.videoUrl}
                poster={c.posterUrl || undefined}
                playsInline
                muted={i !== focus}
                preload="none"
                onEnded={() => i === 0 && setPlaying(false)}
              />
              <span className="scmp-sound" aria-hidden>
                <Icon name={i === focus && c.hasAudio ? "volume" : "mute"} size={14} />
              </span>
            </button>
            <figcaption>
              <b>{c.title}</b>
              <span>{c.detail}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      <button data-action="showcase.compare" type="button" className="btn btn-primary btn-small scmp-play" onClick={playAll}>
        <Icon name={playing ? "pause" : "play"} size={16} /> {playing ? "Pause" : "Play all"}
      </button>
    </section>
  );
}
